import type {
  ConfigurationStatement,
  ConfigurationStatementInput,
  FloorPlannerComposite,
  FloorPlannerCompositeRef,
  FloorPlannerLayout,
  StatementCompositeInput,
  StatementCompositeTarget,
  StatementObserver,
  StatementObserverInput,
} from '@repo/frontend-types';
import {
  asArray,
  floorEntityBid,
  isLive,
  layoutCurrentDesigns,
  observerTargetNamesByRefid,
  withFloorTargetBids,
} from '@repo/front-end-utils';

import { LIVE_OBSERVER_MARKER } from './matchObserversToPlan';
import { selectFileableObservers } from './observerConfigDiff';

/**
 * Turn the plan into the statement Go Live would file — or into a refusal naming what is wrong.
 *
 * ## Why this exists as a pure function
 *
 * It is the inverse of the two match functions beside it (`matchObserversToPlan`,
 * `matchCompositesToPlan`), and the property the whole feature turns on — that reading a gym onto
 * the plan and filing it back changes nothing — is only testable if both directions are pure. The
 * round trip is asserted against the real captured staging gym in this module's test, over 151
 * observers, before a button exists that could write one.
 *
 * ## The thing it carries that the plan does not know
 *
 * An observer on the plan knows what it watches. It does not know **which numbered camera it is**,
 * nor **how finely it was tuned**, and both are identities the data platform is already keyed on.
 * Filing without them re-allocates ordinals from row order — on the staging gym, six of eight
 * multi-camera entities are stored out of ordinal order, so `.1`'s region would be handed to `.2`
 * and two live series would swap contents — and resets a camera somebody deliberately tuned
 * (`UGG-FIN-E113` runs at a body confidence of 0.35 against the other 146 at 0.5).
 *
 * So they are recovered from the **live configuration's statement**, by the observer's database id,
 * which is already carried in its plan refid (`{targetRefid}:live-{id}`).
 *
 * ⚠ **They are deliberately NOT carried on the plan**, and that is a decision rather than a
 * convenience. The plan would then be a second home for identity the database owns, and a stale
 * one: a plan saved before somebody re-tuned a camera in admin would file the old value back over
 * the new one at the next Go Live. Reading them at filing time is the only source that cannot be
 * stale.
 *
 * ## Nothing is dropped
 *
 * A composite whose target or input does not resolve to a BID **refuses the whole build**, naming
 * it. It is not filed without that input, because a total quietly missing a member reports a
 * smaller number and nothing says so — which is the defect this epic exists to remove.
 */

export interface BuildConfigurationStatementParams {
  /** The plan as it stands, from the store — never a snapshot taken earlier in the run. */
  layout: FloorPlannerLayout | null | undefined;
  /** planner refid → minted entity bID, from `convertPlanToTwin`. */
  refidToBid: Record<string, string>;
  /** The twin's own BID, for deriving floor identifiers. */
  twinBid: string;
  /** Name for the new configuration version. */
  configName: string;
  /**
   * The statement of the configuration the twin is measuring RIGHT NOW, or `null` where the twin
   * has no active configuration and there is therefore nothing to carry forward.
   *
   * ⚠ `null` means "this twin has never been filed", NOT "we could not read it". A read that failed
   * must be refused by the caller before it gets here — filing an existing gym with no ordinals is
   * exactly the failure this parameter exists to prevent, and it would look like a clean run.
   */
  live: ConfigurationStatement | null;
  /**
   * The renumberings Go Live captured before anything was stamped (`floor-plan-page-launch-bid-changes`,
   * R11, R14) — full BIDs, `effectiveCodeChanges`' output. Sent so the middleware carries the
   * renumbered spaces' observer and composite identifiers across. Omitted from the statement when
   * empty, so a filing with none is byte-identical to today's. The pre-flight's comparison build does
   * not pass it: nothing is filed there.
   */
  entityCodeChanges?: readonly { from: string; to: string }[];
}

export type BuildConfigurationStatementResult =
  | {
      ok: true;
      statement: ConfigurationStatementInput;
      /**
       * Observers on the plan that will NOT be filed — no region, no stream, or a target the
       * conversion did not mint.
       *
       * Reported because the success summary names them, and computed here rather than by the
       * caller so there is one application of `selectFileableObservers` per Go Live rather than
       * two that could disagree.
       */
      skipped: number;
      /**
       * How many filed observers carried an identity they already had.
       *
       * ⚠ Reported because it is the invariant the whole feature turns on, and the one thing that
       * cannot be told apart afterwards from a clean launch: a filing where every camera was
       * renumbered and a filing where none was look identical in the success summary, and produce
       * the same `observersWritten`. The caller logs it against the total.
       */
      identitiesCarried: number;
    }
  | { ok: false; reasons: string[] };

/**
 * What an observer already is, keyed by its database id.
 *
 * The ordinal is read from `storedEntityBid`'s trailing `.N` rather than from `entityBid`, because
 * `entityBid` is the folded base and the suffix is the whole point — see `StatementObserver`.
 */
interface CarriedIdentity {
  ordinal?: number;
  confidence?: { body: number; head: number };
  estimationInterval?: number;
}

const ORDINAL_SUFFIX = /\.(\d+)$/;

/**
 * One loaded observer's stored camera number — the `.N` on its `storedEntityBid` — or `undefined`.
 *
 * ONE parser, two readers: the filing below carries these numbers across a re-file, and the
 * observer editor's hover tip shows them (through `storedOrdinalById`). Two copies of "what counts
 * as a stored number" would be two answers to whether the tip and Go Live agree on a camera's name.
 *
 * Only a whole number of 1 or more is a number: that is all the server accepts, and an ordinal it
 * would refuse is not an identity worth preserving (or showing) — let the server allocate a fresh
 * one instead of refusing the entire gym over a value nothing can act on.
 */
export const storedOrdinalOf = (observer: StatementObserver): number | undefined => {
  const suffix = ORDINAL_SUFFIX.exec(observer?.storedEntityBid ?? '');
  if (!suffix) return undefined;
  const ordinal = Number(suffix[1]);
  return Number.isInteger(ordinal) && ordinal >= 1 ? ordinal : undefined;
};

/**
 * Every loaded observer's stored camera number, keyed by database id; an observer with no usable
 * suffix has no entry. Built from `storedOrdinalOf`, row by row, so where a statement ever carried
 * one id twice the LAST row decides — exactly as `carriedIdentities` decides.
 */
export const storedOrdinalById = (live: ConfigurationStatement | null): Map<number, number> => {
  const ordinals = new Map<number, number>();
  if (!live) return ordinals;
  for (const observer of asArray<StatementObserver>(live.observers)) {
    if (typeof observer?.id !== 'number') continue;
    const ordinal = storedOrdinalOf(observer);
    if (ordinal === undefined) ordinals.delete(observer.id);
    else ordinals.set(observer.id, ordinal);
  }
  return ordinals;
};

const carriedIdentities = (live: ConfigurationStatement | null): Map<number, CarriedIdentity> => {
  const carried = new Map<number, CarriedIdentity>();
  if (!live) return carried;

  for (const observer of asArray<StatementObserver>(live.observers)) {
    if (typeof observer?.id !== 'number') continue;

    const identity: CarriedIdentity = {};

    const ordinal = storedOrdinalOf(observer);
    if (ordinal !== undefined) identity.ordinal = ordinal;

    // `null` is "not recorded", and omitting it is what makes the server apply its default. Passing
    // null through would be a caller asserting a value it does not have.
    if (observer.confidence) identity.confidence = observer.confidence;
    if (typeof observer.estimationInterval === 'number') {
      identity.estimationInterval = observer.estimationInterval;
    }

    carried.set(observer.id, identity);
  }

  return carried;
};

/**
 * The database id an observer refid carries, or `null` for one drawn in this session.
 *
 * `{targetRefid}:live-{id}` is set by `matchObserversToPlan` when it loads the running
 * configuration onto the plan; an observer the operator drew has `{targetRefid}:{polygonId}` and no
 * marker. That distinction is the one signal telling "this camera already exists and has an
 * identity" from "this camera is new and needs one", and it is why the marker is exported from one
 * place rather than spelled inline.
 */
export const liveObserverId = (refid: string): number | null => {
  const at = refid.lastIndexOf(LIVE_OBSERVER_MARKER);
  if (at === -1) return null;
  const id = Number(refid.slice(at + LIVE_OBSERVER_MARKER.length));
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * How a complex measure is named in a refusal, so the operator can find the one that is broken.
 *
 * Its target is the thing a person recognises — a total is "the total for Zone 3" long before it is
 * a refid — so the target's own identifier leads, with the plan refid as the fallback for a target
 * that is itself unresolvable.
 */
const describeComposite = (composite: FloorPlannerComposite): string => {
  const target = composite.target;
  if (target.kind === 'twin') return 'for the whole twin';
  if (target.kind === 'floor') return `on floor ${target.floorId}`;
  return `on "${target.refid}"`;
};

/** Floor id → the floor's name, for deriving a floor's BID. */
const floorNamesById = (layout: FloorPlannerLayout | null | undefined): Map<number, string> => {
  const names = new Map<number, string>();
  for (const floor of asArray(layout?.floors)) {
    if (typeof floor?.id === 'number' && typeof floor.name === 'string') {
      names.set(floor.id, floor.name);
    }
  }
  return names;
};

/**
 * What `computeObserverLoss` needs to describe a running configuration.
 *
 * The pre-flight now reads the statement rather than the observers route, so the loss computation
 * has to be fed from it. This is a projection and nothing more — the three fields the loss actually
 * reads, in the shape it reads them.
 */
export const statementObserversAsRunning = (
  live: ConfigurationStatement | null,
): { twin_entity_bid: string; rtsp_endpoint_id: number | null; polygon_config: string | null }[] =>
  asArray<StatementObserver>(live?.observers).map(observer => ({
    // The STORED identifier, suffix and all — `computeObserverLoss` folds it itself, and handing it
    // the already-folded `entityBid` would fold twice. Harmless today and wrong the moment the fold
    // normalises anything.
    twin_entity_bid: observer.storedEntityBid,
    rtsp_endpoint_id: observer.rtspEndpointId,
    // `isMeasuring` asks only whether a region exists, so the serialised polygon is not needed —
    // only the presence of one. `null` where the statement could not read it, which is the same
    // thing the observer row would have said.
    polygon_config: observer.polygon ? JSON.stringify(observer.polygon) : null,
  }));

export const buildConfigurationStatement = ({
  layout,
  twinBid,
  configName,
  live,
  refidToBid: convertedRefidToBid,
  entityCodeChanges,
}: BuildConfigurationStatementParams): BuildConfigurationStatementResult => {
  const reasons: string[] = [];

  /*
   * ⛔ **Floors are targets too** (`refiling-preserves-configuration`, epic ledger row 121). The
   * converter mints BIDs for surfaces and items only, so without this every floor-wide observer
   * fails `selectFileableObservers`' `refidToBid` check and is silently left out of the filing —
   * fifteen hand-drawn regions on staging PUL-CON. Idempotent, so a caller that already added them
   * (the Go Live pre-flight does, so its loss count and this filing agree) gets the same map.
   */
  const refidToBid = withFloorTargetBids(convertedRefidToBid, layout, twinBid);

  /*
   * The same predicate the pre-flight counts the loss with, reused rather than re-derived.
   *
   * Two copies is the characteristic defect here: a statement counting what is DRAWN while the
   * filing writes what is FILEABLE understates the loss the operator was asked to accept.
   */
  const planObservers = layoutCurrentDesigns(layout)
    .flatMap(design => asArray(design?.observers))
    .filter(isLive);
  const fileable = selectFileableObservers({ observers: planObservers, refidToBid });

  const carried = carriedIdentities(live);

  /*
   * ⚠ **A camera that claims an identity the running configuration does not have STOPS the filing.**
   *
   * `liveObserverId` reads the database id the plan has been carrying since the page loaded;
   * `carried` holds what the configuration is running RIGHT NOW. They disagree when the twin has
   * been re-filed from somewhere else — admin, another tab — since this wizard opened.
   *
   * Filing anyway is the exact failure FR3 exists to prevent, and it is invisible: every lookup
   * misses, every observer is filed with no ordinal, the server allocates fresh ones from row
   * order, and two live series swap contents while the success summary reads completely normally.
   * There is no signal in the counts, because the same number of observers is written either way.
   *
   * So it refuses and says to reload.
   *
   * ⚠ **It does NOT skip the check when nothing is running**, and an earlier version did. The
   * reasoning was that a twin with no active configuration is a first launch — but a first launch
   * has no `:live-` markers on any refid at all, because nothing was ever loaded onto the plan.
   * A plan carrying markers while the twin has no active configuration is the opposite: the
   * configuration those cameras came from was deactivated mid-session. Filing then drops every
   * ordinal, which is the series swap this exists to prevent. The marker is the signal, not the
   * presence of a live statement.
   */
  const staleIdentities: number[] = [];
  let identitiesCarried = 0;

  const observers: StatementObserverInput[] = fileable.map(observer => {
    const liveId = liveObserverId(observer.refid);
    const identity = liveId === null ? undefined : carried.get(liveId);
    if (liveId !== null) {
      /*
       * ⚠ `identitiesCarried` counts what was actually CARRIED, not what was recognised. An
       * observer with no ordinal, no tuning and no interval resolves to an empty identity — real,
       * but nothing travels with it. Counting those as carried made the log read
       * "147 carried, 0 new" for a filing in which nothing was preserved, which is exactly the
       * reading the number exists to make impossible.
       */
      if (identity) {
        if (identity.ordinal !== undefined) identitiesCarried += 1;
      } else {
        staleIdentities.push(liveId);
      }
    }

    return {
      // The BASE entity BID. `selectFileableObservers` has already established this is present.
      entityBid: refidToBid[observer.targetRefid],
      ...(identity?.ordinal !== undefined ? { ordinal: identity.ordinal } : {}),
      // `selectFileableObservers` requires a stream, so this is never null in practice — but the
      // contract admits null and coercing it to a number here would invent one.
      rtspEndpointId: observer.rtspEndpointId ?? null,
      polygon: observer.points.map(point => ({ x: point.x, y: point.y })),
      ...(observer.trackSide ? { trackSide: observer.trackSide } : {}),
      ...(identity?.confidence ? { confidence: identity.confidence } : {}),
      ...(identity?.estimationInterval !== undefined
        ? { estimationInterval: identity.estimationInterval }
        : {}),
    };
  });

  if (staleIdentities.length > 0) {
    reasons.push(
      `${staleIdentities.length} of the cameras on this plan were loaded from a configuration ` +
        'this twin is no longer measuring, so filing now would give every camera a new identity ' +
        'and move existing data between them. Reload the page to pick up what the twin is ' +
        'measuring now, then try again.',
    );
  }

  const floorNames = floorNamesById(layout);

  /** Resolve one plan reference to the statement target it stands for, or say why it cannot be. */
  const resolve = (
    ref: FloorPlannerCompositeRef,
  ): { ok: true; value: StatementCompositeTarget } | { ok: false; missing: string } => {
    if (ref.kind === 'twin') return { ok: true, value: { kind: 'twin' } };

    if (ref.kind === 'floor') {
      const rawName = floorNames.get(ref.floorId);
      // Named, not described: on a twin with fourteen complex measures, "a floor this plan no
      // longer has" is the same sentence for every one of them and identifies nothing.
      if (rawName === undefined) {
        return { ok: false, missing: `a floor this plan no longer has (floor ${ref.floorId})` };
      }
      /*
       * `{twinBid}-{floorName}`, trimmed — `floorEntityBid`, the ONE copy of the rule, shared with
       * floor-wide OBSERVERS (`withFloorTargetBids`) so a floor total and the floor's own cameras
       * always name the same target. See that function for why the trim matters. The server accepts
       * a floor target whose BID is under this twin and whose tail is not an entity pattern
       * (`resolveTargetBid`), which this satisfies.
       */
      const bid = floorEntityBid(twinBid, rawName);
      if (bid === null) {
        return { ok: false, missing: `a floor with no name (floor ${ref.floorId})` };
      }
      return {
        ok: true,
        value: { kind: 'floor', bid, floorName: rawName.trim() },
      };
    }

    const bid = refidToBid[ref.refid];
    return bid
      ? { ok: true, value: { kind: 'entity', bid } }
      : { ok: false, missing: `an entity this plan does not have (${ref.refid})` };
  };

  const composites: StatementCompositeInput[] = [];

  for (const composite of asArray(layout?.composites).filter(isLive)) {
    const target = resolve(composite.target);
    if (!target.ok) {
      // Named and refused, never dropped: a total filed without a member reports a smaller number
      // and nothing says so.
      reasons.push(
        `The composite measure ${describeComposite(composite)} is a total for ${target.missing}, ` +
          'so it cannot be filed.',
      );
      continue;
    }

    const inputs: StatementCompositeTarget[] = [];
    let missingInput: string | null = null;
    for (const input of asArray(composite.inputs)) {
      const resolved = resolve(input);
      if (!resolved.ok) {
        missingInput = resolved.missing;
        break;
      }
      inputs.push(resolved.value);
    }

    if (missingInput !== null) {
      reasons.push(
        `The composite measure ${describeComposite(composite)} adds up ${missingInput}, so it ` +
          'cannot be filed without changing what it measures.',
      );
      continue;
    }

    composites.push({
      target: target.value,
      inputs,
      // Absent reads as false — which is what every composite placed before the field existed
      // actually is, since the read fold strips a target from its own inputs.
      includesOwnObservers: composite.includesOwnObservers ?? false,
      ...(composite.excluded !== undefined ? { excluded: composite.excluded } : {}),
    });
  }

  if (reasons.length > 0) return { ok: false, reasons };

  /*
   * Display names by BID, from the plan's CURRENT names.
   *
   * This is where every observer's name comes from — the server derives `{entity name}` or
   * `{entity name}.{N}` from it. Taken from the plan at this moment rather than from anything
   * stored on the observer, because a placed item's name is a value the Asset Register owns and can
   * change after the polygon was drawn.
   */
  const entityNames: Record<string, string> = {};
  // Floors included, so a floor-wide observer is named for its floor as a zone's is for its zone.
  observerTargetNamesByRefid(layout).forEach((name, refid) => {
    const bid = refidToBid[refid];
    if (bid) entityNames[bid] = name;
  });

  return {
    ok: true,
    skipped: planObservers.length - fileable.length,
    identitiesCarried,
    statement: {
      name: configName,
      observers,
      composites,
      entityNames,
      ...(entityCodeChanges && entityCodeChanges.length > 0
        ? { entityCodeChanges: entityCodeChanges.map(({ from, to }) => ({ from, to })) }
        : {}),
    },
  };
};

/**
 * The renumberings worth sending: those whose `from` the ACTIVE configuration still files something
 * under — an observer on it (or on `from.N`), or a composite targeting or adding it up
 * (`floor-plan-page-launch-bid-changes`, Copilot on #2115, round 3). That configuration is the durable
 * record of whether a renumbering has been carried: after a filing that failed, it still holds `from`
 * and the renumbering is sent again; after one that succeeded but whose clearing save was lost, it holds
 * `to` and the stale entry is dropped. A dropped entry carries nothing because nothing is held under
 * `from`, so dropping it is always safe; sending it would claim a target the configuration already
 * holds and cost that space its identifiers. `null` — a twin never filed — holds nothing.
 */
export const renumberingsTheConfigurationHolds = <T extends { from: string }>(
  changes: readonly T[],
  live: ConfigurationStatement | null,
): T[] => {
  if (changes.length === 0 || !live) return [];
  const fold = (bid: string | null | undefined) =>
    (bid ?? '')
      .trim()
      .toUpperCase()
      .replace(/\.\d+$/, '');
  const held = new Set<string>();
  live.observers.forEach(o => held.add(fold(o.entityBid)));
  live.composites.forEach(c => {
    [c.target, ...c.inputs].forEach(t => {
      if (t.kind === 'entity') held.add(fold(t.bid));
    });
  });
  return changes.filter(c => held.has(fold(c.from)));
};
